/**
 * Value formatting helpers: currency, percentage, and dates.
 */

/**
 * Format a number as currency.
 * @param {number} value
 * @param {string} [currency='USD']
 * @param {string} [locale='en-US']
 * @returns {string}
 */
export function formatCurrency(value, currency = 'USD', locale = 'en-US') {
  if (value == null || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a numeric ratio or percentage value.
 * @param {number} value - Either a fraction (0.42) or a whole percent (42).
 * @param {Object} [options]
 * @param {boolean} [options.isFraction=false] - Treat value as 0-1 fraction.
 * @param {number} [options.decimals=1]
 * @returns {string}
 */
export function formatPercent(value, { isFraction = false, decimals = 1 } = {}) {
  if (value == null || Number.isNaN(value)) return '-';
  const pct = isFraction ? value * 100 : value;
  return `${pct.toFixed(decimals)}%`;
}

/**
 * Format a compact number, e.g. 12_500 -> "12.5K".
 * @param {number} value
 * @param {string} [locale='en-US']
 * @returns {string}
 */
export function formatNumber(value, locale = 'en-US') {
  if (value == null || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Format a date value as a short readable date.
 * @param {Date|string|number} value
 * @param {string} [locale='en-US']
 * @returns {string}
 */
export function formatDate(value, locale = 'en-US') {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Format a value according to a KPI format tag.
 * @param {number} value
 * @param {'currency'|'percent'|'number'} format
 * @returns {string}
 */
export function formatByType(value, format) {
  switch (format) {
    case 'currency':
      return formatCurrency(value);
    case 'percent':
      return formatPercent(value);
    default:
      return formatNumber(value);
  }
}

/**
 * Achievement tone thresholds shared across dashboards (promoted from the
 * legacy Brand page): red below 80, orange 80–90, green above 90.
 *
 * Returns an MUI theme color token (`error.main` / `warning.main` /
 * `success.main`) by default so existing `sx={{ color: achievementColor(v) }}`
 * usages keep working. Pass `'hex'` to get concrete hex values suitable for
 * standalone pill badges (text + light background), or `'tone'` for the raw
 * semantic name.
 *
 * @param {number|string} value - Achievement percentage.
 * @param {'token'|'hex'|'tone'} [as='token']
 * @returns {string | { tone: string, color: string, bg: string }}
 */
export function achievementColor(value, as = 'token') {
  const n = Number(value);
  let tone;
  if (!Number.isFinite(n) || n < 80) tone = 'error';
  else if (n <= 90) tone = 'warning';
  else tone = 'success';

  if (as === 'tone') return tone;
  if (as === 'hex') {
    // { text color, light background } per tone — matches the reference pills.
    const map = {
      error: { tone, color: '#DC2626', bg: '#FEE2E2' }, // red
      warning: { tone, color: '#D97706', bg: '#FEF3C7' }, // orange
      success: { tone, color: '#16A34A', bg: '#DCFCE7' }, // green
    };
    return map[tone];
  }
  return `${tone === 'error' ? 'error' : tone === 'warning' ? 'warning' : 'success'}.main`;
}
